import { useMemo, useState, type JSX } from 'react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { FormField } from '@/components/form';
import {
  PROPERTY_HERO_MEDIA_ALLOWED_TYPES,
  PROPERTY_MEDIA_MAX_BYTES,
  PROPERTY_MEDIA_MAX_FILES,
} from '@/lib/constants/propertyMedia';
import {
  isAllowedPropertyHeroMediaType,
  isPropertyMediaVideoUrl,
  isWithinPropertyMediaSizeLimit,
  parsePropertyMediaUrl,
  removePropertyMediaFiles,
  uploadPropertyMediaFile,
} from '@/lib/storage/propertyMedia';

interface PropertyEditHeroMediaSectionProps {
  propertyId: string;
  heroMediaUrls: string[];
  disabled: boolean;
  error?: string;
  onHeroMediaUrlsChange: (updater: (current: string[]) => string[]) => void;
  onClearError: () => void;
  onUploadingChange: (uploading: boolean) => void;
}

export function PropertyEditHeroMediaSection({
  propertyId,
  heroMediaUrls,
  disabled,
  error,
  onHeroMediaUrlsChange,
  onClearError,
  onUploadingChange,
}: PropertyEditHeroMediaSectionProps): JSX.Element {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const maxImageSizeGb = useMemo(
    () => Math.round(PROPERTY_MEDIA_MAX_BYTES / (1024 * 1024 * 1024)),
    [],
  );
  const acceptTypes = useMemo(() => PROPERTY_HERO_MEDIA_ALLOWED_TYPES.join(','), []);

  const mediaList = useMemo(
    () => heroMediaUrls.map((url) => url.trim()).filter((url) => url.length > 0),
    [heroMediaUrls],
  );
  const isGalleryFull = mediaList.length >= PROPERTY_MEDIA_MAX_FILES;
  const canInteract = !disabled && !isUploading && !isGalleryFull;

  const setUploading = (next: boolean) => {
    setIsUploading(next);
    onUploadingChange(next);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const selectedFiles = Array.from(event.target.files ?? []);
    if (selectedFiles.length === 0) {
      return;
    }

    setUploadError(null);

    const remainingSlots = PROPERTY_MEDIA_MAX_FILES - mediaList.length;
    if (remainingSlots <= 0) {
      setUploadError(`You can upload up to ${PROPERTY_MEDIA_MAX_FILES.toString()} media files.`);
      event.target.value = '';
      return;
    }

    const files = selectedFiles.slice(0, remainingSlots);
    if (selectedFiles.length > remainingSlots) {
      toast.info(
        `Uploading ${remainingSlots.toString()} of ${selectedFiles.length.toString()} selected files.`,
      );
    }

    const invalidType = files.find((file) => !isAllowedPropertyHeroMediaType(file));
    if (invalidType) {
      setUploadError(`Unsupported file type: ${invalidType.type || invalidType.name}.`);
      event.target.value = '';
      return;
    }

    const tooLarge = files.find((file) => !isWithinPropertyMediaSizeLimit(file));
    if (tooLarge) {
      setUploadError(`Each media file must be under ${maxImageSizeGb.toString()}GB.`);
      event.target.value = '';
      return;
    }

    setUploading(true);
    let uploadedCount = 0;

    try {
      for (const file of files) {
        const result = await uploadPropertyMediaFile({ propertyId, file });
        if (typeof result.url === 'string' && result.url.length > 0) {
          uploadedCount += 1;
          onHeroMediaUrlsChange((current) => [...current, result.url]);
          onClearError();
        }
      }

      if (uploadedCount === 1) {
        toast.success('Uploaded media.');
      } else if (uploadedCount > 1) {
        toast.success(`Uploaded ${uploadedCount.toString()} media files.`);
      }
    } catch (uploadFailure) {
      const message =
        uploadFailure instanceof Error ? uploadFailure.message : 'Upload failed. Please try again.';
      setUploadError(message);
      toast.error(message);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveMedia = async (url: string): Promise<void> => {
    setUploadError(null);
    setUploading(true);
    let removed = false;
    try {
      const parsed = parsePropertyMediaUrl(url);
      if (parsed) {
        await removePropertyMediaFiles([`${parsed.bucket}/${parsed.objectPath}`]);
      }
      removed = true;
    } catch (removeFailure) {
      const message =
        removeFailure instanceof Error
          ? removeFailure.message
          : 'Unable to remove media from storage.';
      setUploadError(message);
      toast.error(message);
    } finally {
      if (removed) {
        onHeroMediaUrlsChange((current) => current.filter((existing) => existing !== url));
        onClearError();
        toast.success('Removed media.');
      }
      setUploading(false);
    }
  };

  return (
    <FormField
      id="property-hero-media"
      label="Hero media"
      description={`Optional. Shown in the public property hero gallery. Upload up to ${PROPERTY_MEDIA_MAX_FILES.toString()} photos or MP4 videos. Max ${maxImageSizeGb.toString()}GB each.`}
      error={uploadError ?? error}
    >
      <div className="flex flex-col gap-4">
        <label
          className={clsx(
            'inline-flex w-fit items-center justify-center rounded-full border border-surface-subtle px-4 py-2 text-sm font-semibold text-brand-primary transition focus-within:outline-none focus-within:ring-2 focus-within:ring-brand-primary/40 hover:bg-brand-primary/5',
            (disabled || isUploading) && 'opacity-60',
          )}
        >
          <input
            type="file"
            accept={acceptTypes}
            className="sr-only"
            multiple
            disabled={!canInteract}
            onChange={handleFileChange}
          />
          {isUploading ? 'Uploading…' : isGalleryFull ? 'Gallery full' : 'Upload media'}
        </label>

        {mediaList.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {mediaList.map((url) => (
              <div
                key={url}
                className="flex flex-col overflow-hidden rounded-2xl border border-surface-subtle bg-white"
              >
                {isPropertyMediaVideoUrl(url) ? (
                  <video
                    src={url}
                    className="h-40 w-full object-cover"
                    controls
                    preload="metadata"
                    playsInline
                    aria-label="Property hero video"
                  />
                ) : (
                  <img src={url} alt="Property hero" className="h-40 w-full object-cover" />
                )}
                <button
                  type="button"
                  className="border-t border-surface-subtle px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-60"
                  disabled={disabled || isUploading}
                  onClick={() => {
                    void handleRemoveMedia(url);
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-text-primary/70">
            No hero media yet. Upload photos or MP4 videos to populate the property hero gallery.
          </p>
        )}
      </div>
    </FormField>
  );
}
